/* eslint-disable max-len */
import { handle } from 'redux-pack';
import { isEqual } from 'underscore';
import {
	LOAD_GENERATE_CONTENT_TEMPLATES,
	LOAD_GENERATED_CONTENT,
	LOAD_GENERATED_CONTENT_BY_ID,
	REMOVE_GENERATED_CONTENT_BY_ID,
	CONTENT_ENTITY_UPDATED,
	CONTENT_ENTITY_UPDATED_DRAFT,
	LOAD_DRAFT_CONTENT,
	CLEAR_DRAFT_CONTENT,
	SET_SELECTED_MEDIA_TEMPLATE,
	SET_STYLE_PACK_DRAWER,
	UPSERT_CONTENT,
	ADD_INITIALIZING_JOB,
} from '../actions/generate-content';
import { GraphicInterface } from './graphics';
import { MediaItem } from './media';

export interface TemplateData {
	apiVersion: string;
    thumbnail: {
        id: string;
        url: string;
        title: string;
        width: number;
        height: number;
        contentType: string;
    }
    variables: {
       name: string;
       type: string;
       options: { [key: string]: any };
       required: boolean;
       userData: string;
       validation: any;
    }[]
}

export interface ContentTemplate {
    id: number;
	contentType?: string; // backwards compatibility assume if empty is title
	videoUrl?: string; // backwards compatibility it might not be here
	thumbnailUrl?: string; // backwards compatibility it might not be here
	stylePack: string;
    type: string;
    data: TemplateData;
    provider: string;
    name: string;
	locked: boolean;
}

export interface GeneratedContentData {
    key: string;
    value: string;
}

export enum ContentServiceJobStatus {
	completed = 'completed',
	processing = 'processing',
}

export interface GeneratedContent {
    id?: number;
    templateId: number;
    dateCreated?: Date;
    createdBy?: number;
    dateUpdated?: Date;
    updatedBy?: number;
    jobId?: string;
	jobStatus?: ContentServiceJobStatus;
    contentUrl?: string;
    provider: string;
    type: string;
    fkTable: FkTable;
    fkId?: number;
    data: GeneratedContentData[];
    version: number;
	apiVersion?: string;
}

export enum FkTable {
	graphics = 'gr_graphics',
	media = 'gr_media',
}

export interface GeneratedContentRequest {
    templateId: number;
    id: number;
	fkTable: FkTable;
    fkId?: number;
    version: number;
    graphic?: GraphicInterface;
    mediaItem?: MediaItem;
    data: GeneratedContentData[];
    contentUrl: string;
    jobId?: string;
    jobStatus?: string;
}

type InitializingJobMap = Map<string, GeneratedContentData[]>;

export interface GeneratedContentState {
    contentTemplates: ContentTemplate[];
    generatedContent: GeneratedContent[];
	generatedContentEdits: GeneratedContent[];
    loadingTemplates: boolean;
    loadingGeneratedContent: boolean;
	selectedMediaItem: MediaItem | null;
	selectedMediaTemplate: string;
	selectedStylePackDrawer: 'style-packs' | 'style-pack-info';
	initializingJobs: InitializingJobMap;
}

export const initialGeneratedContentState: GeneratedContentState = {
	contentTemplates: [],
	generatedContent: [],
	generatedContentEdits: [],
	loadingTemplates: false,
	loadingGeneratedContent: false,
	selectedMediaItem: null,
	selectedMediaTemplate: '',
	selectedStylePackDrawer: 'style-packs',
	initializingJobs: new Map(),
};

interface GeneratedContentAction {
    type: string;
    payload: any;
    id?: number;
}

const reduceFunction = (acc: GeneratedContent[], content: GeneratedContent) => {
	// first value is always going to be the new item
	if (acc[0].id === content.id) {
		const existingContent = acc.shift();
		acc.push(existingContent ?? content);
	} else {
		acc.push(content);
	}
	return acc;
};

const removeInitializingJob = (jobs: InitializingJobMap, content: GeneratedContent, isDraft: boolean) => {
	const newJobs = new Map(jobs);
	const key = `${content.fkTable}-${content.fkId}${isDraft ? '-draft' : ''}`;
	const existingJob = newJobs.get(key);
	if (existingJob && isEqual(existingJob, content.data)) {
		newJobs.delete(key);
	}
	return newJobs;
};

const actions: Map<any, Function> = new Map([
	[
		LOAD_GENERATE_CONTENT_TEMPLATES,
		(state: GeneratedContentState, action: GeneratedContentAction) => handle(state, action, {
			start: (s:GeneratedContentState) => ({
				...s,
				loadingTemplates: true,
			}),
			finish: (s:GeneratedContentState) => ({
				...s,
				loadingTemplates: false,
			}),
			failure: (s:GeneratedContentState) => ({
				...s,
				loadingTemplates: false,
			}),
			success: (s:GeneratedContentState) => {
				const contentTemplates = action.payload?.templates ?? [];
				return {
					...s,
					contentTemplates,
				};
			},
		}),
	],
	[
		LOAD_GENERATED_CONTENT,
		(state: GeneratedContentState, action: GeneratedContentAction) => handle(state, action, {
			start: (s:GeneratedContentState) => ({
				...s,
				loadingGeneratedContent: true,
			}),
			finish: (s:GeneratedContentState) => ({
				...s,
				loadingGeneratedContent: false,
			}),
			failure: (s:GeneratedContentState) => ({
				...s,
				loadingGeneratedContent: false,
			}),
			success: (s:GeneratedContentState) => {
				const generatedContent = action.payload?.generatedContent ?? [];
				return {
					...s,
					generatedContent,
				};
			},
		}),
	],
	[
		LOAD_GENERATED_CONTENT_BY_ID,
		(state: GeneratedContentState, action: GeneratedContentAction) => handle(state, action, {
			start: (s:GeneratedContentState) => s,
			finish: (s:GeneratedContentState) => s,
			failure: (s:GeneratedContentState) => s,
			success: (s:GeneratedContentState) => {
				const generatedContent = [...s.generatedContent];
				const index = generatedContent?.findIndex(
					(content: GeneratedContent) => content.id === action?.payload?.generatedContent?.id ?? 0,
				) ?? -1;

				if (index > -1) {
					if (generatedContent[index].version <= action?.payload?.generatedContent?.version) {
						generatedContent[index] = action.payload?.generatedContent ?? {};
					} else {
						console.log(`current version ${generatedContent[index].version} is greater than ${action?.payload?.generatedContent?.version} and ignoring update`);
					}
				} else if (action.payload?.generatedContent?.id) {
					// put it at the top of the list
					generatedContent.unshift(action.payload?.generatedContent ?? {});
				}

				return {
					...s,
					generatedContent,
				};
			},
		}),
	],
	[
		REMOVE_GENERATED_CONTENT_BY_ID,
		(state: GeneratedContentState, action: GeneratedContentAction) => handle(state, action, {
			start: (s:GeneratedContentState) => s,
			finish: (s:GeneratedContentState) => s,
			failure: (s:GeneratedContentState) => s,
			success: (s:GeneratedContentState) => {
				const generatedContent = [...s.generatedContent];
				const index = generatedContent.findIndex(
					(content: GeneratedContent) => content.id === action?.payload ?? 0,
				);
				if (index > -1) {
					generatedContent.splice(index, 1);
				}

				return {
					...s,
					generatedContent,
				};
			},
		}),
	],
	[
		CONTENT_ENTITY_UPDATED,
		(state: GeneratedContentState, action: GeneratedContentAction) => ({
			...state,
			generatedContent: [...state.generatedContent.reduce(
				reduceFunction,
				[action.payload.entityValue],
			)],
			initializingJobs: removeInitializingJob(state.initializingJobs, action.payload.entityValue, false),
		}),
	],
	[
		CONTENT_ENTITY_UPDATED_DRAFT,
		(state: GeneratedContentState, action: GeneratedContentAction) => ({
			...state,
			generatedContentEdits: [...state.generatedContentEdits.reduce(
				reduceFunction,
				[action.payload.entityValue],
			)],
			initializingJobs: removeInitializingJob(state.initializingJobs, action.payload.entityValue, true),
		}),
	],
	[
		CLEAR_DRAFT_CONTENT,
		(state: GeneratedContentState) => ({
			...state,
			generatedContentEdits: [],
		}),
	],
	[
		LOAD_DRAFT_CONTENT,
		(state: GeneratedContentState, action: GeneratedContentAction) => handle(state, action, {
			start: (s) => ({ ...s }),
			finish: (s) => ({ ...s }),
			failure: (s) => ({ ...s }),
			success: (s) => ({ ...s, generatedContentEdits: action.payload }),
		}),
	],
	[
		UPSERT_CONTENT,
		(state: GeneratedContentState, action: GeneratedContentAction) => handle(state, action, {
			start: (s) => ({ ...s }),
			finish: (s) => ({ ...s }),
			failure: (s) => ({ ...s }),
			success: (s) => ({ ...s }),
		}),
	],
	[
		SET_SELECTED_MEDIA_TEMPLATE,
		(state: GeneratedContentState, action: GeneratedContentAction) => ({
			...state,
			selectedMediaTemplate: action.payload.template,
			selectedMediaItem: action.payload.mediaItem,
		}),
	],
	[
		SET_STYLE_PACK_DRAWER,
		(state: GeneratedContentState, action: GeneratedContentAction) => ({
			...state,
			selectedStylePackDrawer: action.payload,
		}),
	],
	[
		ADD_INITIALIZING_JOB,
		(state: GeneratedContentState, action: GeneratedContentAction) => {
			const newInitializingJobs = new Map(state.initializingJobs);
			newInitializingJobs.set(action.payload.key, action.payload.data);

			return {
				...state,
				initializingJobs: newInitializingJobs,
			};
		},
	],
]);

export const GeneratedContentReducer = (
	state: GeneratedContentState = initialGeneratedContentState,
	action: GeneratedContentAction = { type: '', payload: {} },
) => actions.get(action.type)?.(state, action) ?? state;
